// Local draft model for the cycling Make-Your-Picks flow. A cycling stage presents
// exactly CONFIG.CYCLING.DUELS_PER_STAGE balanced duels and you predict ALL of them
// (no "best N of many"), so the draft is the fixed slate of duels with a chosen side
// per duel. The ordered list IS the confidence ranking: index 0 carries the highest
// weight (the "banker"), mirroring the football lib/picks.ts model.
import { CONFIG, type DuelPick, type DuelSide, type Entry, type Fixture } from '@h2h/shared';

/** A single duel in the draft: which side the player is leaning (undefined while choosing). */
export interface DuelDraft {
  duelId: string;
  pick?: DuelSide;
}

/** The cycling confidence ladder (descending), as a plain array for the staker. */
export const CYCLING_WEIGHTS: number[] = [...CONFIG.CYCLING.CONFIDENCE_WEIGHTS];

/** Every duel called? (the slate is fixed, so this is the only completeness gate). */
export function allDuelsPicked(order: DuelDraft[]): boolean {
  return order.length > 0 && order.every((d) => !!d.pick);
}

/**
 * Build engine DuelPicks from the ordered draft. The list order is the confidence
 * ranking — the first duel gets the highest weight. Returns null until every duel
 * has a side and the count matches the ladder (so we never submit a partial slate).
 */
export function toDuelPicks(
  order: DuelDraft[],
  weights: readonly number[] = CONFIG.CYCLING.CONFIDENCE_WEIGHTS
): DuelPick[] | null {
  if (order.length !== weights.length) return null;
  const out: DuelPick[] = [];
  order.forEach((d, i) => {
    if (!d.pick) return;
    out.push({ duelId: d.duelId, pick: d.pick, confidenceWeight: weights[i] });
  });
  return out.length === order.length ? out : null;
}

/** Rebuild an ordered draft from a saved entry (highest confidence first). */
export function draftFromCyclingEntry(entry: Entry): DuelDraft[] {
  return [...(entry.duelPicks ?? [])]
    .sort((a, b) => b.confidenceWeight - a.confidenceWeight)
    .map((p) => ({ duelId: p.duelId, pick: p.pick }));
}

/** Seed an initial (unpicked) draft from the slate's duel fixtures, in slate order. */
export function draftFromDuels(fixtures: Fixture[]): DuelDraft[] {
  return fixtures.map((f) => ({ duelId: f.fixtureId }));
}
